/**
 * ConcurrencyGuard (AJIP-16.02.004 · WS8)
 * ---------------------------------------------------------------------
 * Per Founder Ruling P-03: integrity mechanism is `revision` +
 * `updatedAt` + payload `checksum`. Stale updates cannot silently
 * overwrite newer state.
 *
 * assertMonotonicWrite(previous, next)
 *   * `previous` is the currently-stored record (or null on first write).
 *   * `next` is the proposed record.
 *   * On first write (previous === null) the guard accepts any revision.
 *   * On subsequent write the guard requires:
 *       next.revision === previous.revision + 1
 *       next.updatedAt >= previous.updatedAt
 *     (revision is monotonic; updatedAt is a soft check that the caller
 *     did not send a stale timestamp with a stale revision).
 */
import { ConflictError } from './PersistenceErrors';

const assertMonotonicWrite = (previous, next) => {
  if (!next || typeof next.revision !== 'number' || typeof next.updatedAt !== 'string') {
    throw new ConflictError('Next record lacks revision or updatedAt');
  }
  if (previous === null || previous === undefined) return true;
  if (next.revision !== previous.revision + 1) {
    throw new ConflictError('Stale write: revision must increase by exactly 1', {
      previousRevision: previous.revision,
      nextRevision: next.revision,
    });
  }
  const prevMs = Date.parse(previous.updatedAt);
  const nextMs = Date.parse(next.updatedAt);
  if (Number.isNaN(prevMs) || Number.isNaN(nextMs)) {
    throw new ConflictError('Timestamps are not parseable');
  }
  if (nextMs < prevMs) {
    throw new ConflictError('Stale write: updatedAt moved backwards');
  }
  return true;
};

const bumpRevision = (previous) => (previous ? previous.revision + 1 : 1);

export { assertMonotonicWrite, bumpRevision };
